/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import React, { useState, useEffect } from 'react';
import {
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import {
  useGetSnowflakeDatabaseSchemaQuery,
  useSaveSnowflakeSemanticModelMutation,
  SemanticModel,
  SemanticModelLogicalTable,
  SemanticModelRelationship,
  useGetSnowflakeSemanticModelQuery,
} from '../../../../../../api/initiative-data-integrations';
import { BasicAlert } from '@g17eco/molecules/alert';
import { convertSchemaToSemanticModel, cleanSemanticModelFromDataIntegrationColumns } from '../utils';
import * as YAML from 'yaml';
import { BlockingLoader } from '../../../../../../atoms/loader';
import { TableConfigForm } from './partials/TableConfigForm';
import { RelationshipConfigForm } from './partials/RelationshipConfigForm';
import { useAppSelector } from '../../../../../../reducers';
import { isStaff } from '../../../../../../selectors/user';
import { PreviewAlert } from '@g17eco/molecules/feature-stability/PreviewAlert';
import { addSiteAlert, addSiteError } from '../../../../../../slice/siteAlertsSlice';
import { TabRelationships } from './partials/TabRelationships';
import { parseYaml, validateYamlContent, YamlError } from './utils';
import { TabTables } from './partials/TabTables';
import { TabGeneral } from './partials/TabGeneral';
import { TabYaml } from './partials/TabYaml';

interface SemanticModelConfigTabProps {
  initiativeId: string;
  connectionId: string;
}

export const SemanticModelConfigTab: React.FC<SemanticModelConfigTabProps> = ({
  initiativeId,
  connectionId,
}) => {
  const [activeTab, setActiveTab] = useState<string>('general');
  const [semanticModel, setSemanticModel] = useState<SemanticModel>({
    name: '',
    description: '',
    custom_instructions: '',
    module_custom_instructions: {},
    tables: [],
    relationships: [],
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [editingTable, setEditingTable] = useState<SemanticModelLogicalTable | null>(null);
  const [editingRelationship, setEditingRelationship] = useState<SemanticModelRelationship | null>(null);
  const [yamlContent, setYamlContent] = useState<string>('');
  const [yamlError, setYamlError] = useState<YamlError | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Get semantic models for this connection
  const { data: existingSemanticModel, isLoading: semanticModelLoading, isFetching: semanticModelFetching } = useGetSnowflakeSemanticModelQuery(
    { initiativeId, connectionId },
  );

  // Get database schema for this connection
  const { data: schema, isLoading: schemaLoading } = useGetSnowflakeDatabaseSchemaQuery(
    { initiativeId, connectionId },
  );

  // Save semantic model mutation
  const [saveSemanticModel, { isLoading: isSaving }] = useSaveSnowflakeSemanticModelMutation();

  const isStaffUser = useAppSelector(isStaff);

  // Load existing model when data is available
  useEffect(() => {
    if (existingSemanticModel) {
      try {
        const cleanedModel = cleanSemanticModelFromDataIntegrationColumns(existingSemanticModel);
        setSemanticModel(cleanedModel);
        setYamlContent(YAML.stringify(cleanedModel));
        setHasUnsavedChanges(false);
      } catch (error) {
        setError('Failed to parse existing semantic model, will reset to schema: ' + (error instanceof Error ? error.message : 'Unknown error'));
        const basicModel = convertSchemaToSemanticModel(schema);
        setSemanticModel(basicModel);
        setYamlContent(YAML.stringify(basicModel));
      }
    } else if (schema) {
      const basicModel = convertSchemaToSemanticModel(schema);
      setSemanticModel(basicModel);
      setYamlContent(YAML.stringify(basicModel));
    }
  }, [existingSemanticModel, schema]);

  const handleSave = async () => {
    let modelToSave = semanticModel;

    // If on YAML tab, parse the YAML content first
    if (activeTab === 'yaml') {
      // Check for validation errors first
      if (yamlError) {
        setError('Please fix the YAML syntax errors before saving.');
        return;
      }

      const result = parseYaml(yamlContent);

      if (!result.success) {
        setError('Invalid YAML format. Please fix the syntax before saving.');
        return;
      }

      try {
        modelToSave = cleanSemanticModelFromDataIntegrationColumns(result.data);
        setSemanticModel(modelToSave); // Update the form model too
      } catch (error) {
        setError('Error processing semantic model: ' + (error instanceof Error ? error.message : 'Unknown error'));
        return;
      }
    }

    if (!modelToSave.name.trim()) {
      setError('Model name is required.');
      return;
    }

    try {
      await saveSemanticModel({
        initiativeId,
        connectionId,
        yamlContent: modelToSave,
      }).unwrap();

      // Since the API doesn't return the updated model, we need to refresh it
      // The most reliable way is to invalidate the cache and refetch
      setHasUnsavedChanges(false);
      setError(null);
      addSiteAlert({ content:'Semantic model saved successfully' });
    } catch (error) {
      addSiteError({ content:'Failed to save semantic model: ' + (error instanceof Error ? error.message : 'Unknown error') });
    }
  };

  const handleDiscard = () => {
    // Reload the original content
    if (existingSemanticModel) {
      try {
        const cleanedModel = cleanSemanticModelFromDataIntegrationColumns(existingSemanticModel);
        setSemanticModel(cleanedModel);
        setYamlContent(YAML.stringify(cleanedModel));
      } catch (error) {
        setError('Failed to parse existing semantic model: ' + (error instanceof Error ? error.message : 'Unknown error'));
        return;
      }
    }
    setHasUnsavedChanges(false);
    setActiveTab('general');
    setYamlError(null);
    setError(null);
  };

  const handleResetToSchema = () => {
    if (confirm('Are you sure you want to reset the semantic model to match the current database schema? This will overwrite all current configuration.')) {
      const basicModel = convertSchemaToSemanticModel(schema);
      setSemanticModel(basicModel);
      setYamlContent(YAML.stringify(basicModel));
      setHasUnsavedChanges(true);
      setYamlError(null);
      setError(null);
    }
  };

  const handleEditTable = (table: SemanticModelLogicalTable) => {
    setEditingTable({ ...table });
    setShowTableModal(true);
  };

  const handleSaveTable = () => {
    if (!editingTable) return;

    const updatedTables = [...semanticModel.tables];

    // Find existing table by base_table identity (database.schema.table) instead of name
    // This prevents duplicates when the name is changed
    const existingIndex = updatedTables.findIndex((t) =>
      t.base_table.database === editingTable.base_table.database &&
      t.base_table.schema === editingTable.base_table.schema &&
      t.base_table.table === editingTable.base_table.table
    );

    if (existingIndex >= 0) {
      updatedTables[existingIndex] = editingTable;
    } else {
      updatedTables.push(editingTable);
    }

    setSemanticModel({ ...semanticModel, tables: updatedTables });
    setShowTableModal(false);
    setEditingTable(null);
    setHasUnsavedChanges(true);
  };

  const handleAddRelationship = () => {
    setEditingRelationship({
      name: '',
      left_table: '',
      right_table: '',
      relationship_columns: [{ left_column: '', right_column: '' }],
      join_type: 'left_outer',
      relationship_type: 'many_to_one',
    });
    setShowRelationshipModal(true);
  };

  const handleEditRelationship = (relationship: SemanticModelRelationship) => {
    setEditingRelationship({ ...relationship });
    setShowRelationshipModal(true);
  };

  const handleSaveRelationship = () => {
    if (!editingRelationship) return;

    const updatedRelationships = [...(semanticModel.relationships || [])];
    const existingIndex = updatedRelationships.findIndex((r) => r.name === editingRelationship.name);

    if (existingIndex >= 0) {
      updatedRelationships[existingIndex] = editingRelationship;
    } else {
      updatedRelationships.push(editingRelationship);
    }

    setSemanticModel({ ...semanticModel, relationships: updatedRelationships });
    setShowRelationshipModal(false);
    setEditingRelationship(null);
    setHasUnsavedChanges(true);
  };

  const handleDeleteRelationship = (relationshipName: string) => {
    if (confirm(`Are you sure you want to delete relationship "${relationshipName}"?`)) {
      const updatedRelationships = (semanticModel.relationships || []).filter((r) => r.name !== relationshipName);
      setSemanticModel({ ...semanticModel, relationships: updatedRelationships });
      setHasUnsavedChanges(true);
    }
  };


  // Handle YAML content changes with validation
  const handleYamlContentChange = (content: string) => {
    setYamlContent(content);
    const error = validateYamlContent(content);
    setYamlError(error);
    setHasUnsavedChanges(true);
  };

  if (semanticModelLoading || schemaLoading) {
    return (
      <div className='text-center my-4'>
        <BlockingLoader />
        <div className='mt-2'>Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className='semantic-model-config'>
      <PreviewAlert />
      {/* Save Section */}
      {semanticModelFetching || isSaving ? <BlockingLoader /> : null}
      {hasUnsavedChanges && (
        <div className='alert alert-warning d-flex justify-content-between align-items-center mb-3'>
          <div>
            <i className='fal fa-exclamation-triangle me-2' />
            You have unsaved changes
          </div>
          <div className='d-flex gap-2'>
            <Button color='success' onClick={handleSave} disabled={isSaving || !semanticModel.name.trim()}>
              {isSaving ? (
                <>
                  <i className='fas fa-spinner fa-spin me-1' />
                  Saving...
                </>
              ) : (
                <>
                  <i className='fal fa-save me-1' />
                  Save Changes
                </>
              )}
            </Button>
            <Button color='secondary' onClick={handleDiscard} disabled={isSaving}>
              Discard
            </Button>
            <Button color='warning' onClick={handleResetToSchema} disabled={isSaving || !schema}>
              <i className='fal fa-refresh me-1' />
              Reset to Schema
            </Button>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <Nav tabs className='mb-4 cursor-pointer'>
        <NavItem>
          <NavLink className={activeTab === 'general' ? 'active' : ''} onClick={() => {
            setActiveTab('general');
            setYamlError(null);
          }}>
            <i className='fal fa-info-circle me-1' />
            General
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink className={activeTab === 'tables' ? 'active' : ''} onClick={() => {
            setActiveTab('tables');
            setYamlError(null);
          }}>
            <i className='fal fa-table me-1' />
            Tables ({semanticModel.tables.length})
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={activeTab === 'relationships' ? 'active' : ''}
            onClick={() => {
              setActiveTab('relationships');
              setYamlError(null);
            }}
          >
            <i className='fal fa-link me-1' />
            Relationships ({semanticModel.relationships?.length || 0})
          </NavLink>
        </NavItem>
        {isStaffUser ? (
        <NavItem>
          <NavLink className={activeTab === 'yaml' ? 'active' : ''} onClick={() => {
            setActiveTab('yaml');
            const error = validateYamlContent(yamlContent);
            setYamlError(error);
          }}>
            <i className='fal fa-code me-1' />
            Edit YAML
            {yamlError ? (
              <i className='fal fa-exclamation-triangle text-danger ms-1' title='Syntax errors detected' />
            ) : null}
            {error ? (
              <BasicAlert type='danger'>
                {error}
              </BasicAlert>
            ) : null}
          </NavLink>
        </NavItem>
        ) : null}
      </Nav>

      {/* Tab Content */}
      <TabContent activeTab={activeTab}>
        {/* General Tab */}
        <TabPane tabId='general'>
          <TabGeneral
            semanticModel={semanticModel}
            setSemanticModel={setSemanticModel}
            setHasUnsavedChanges={setHasUnsavedChanges}
          />
        </TabPane>

        {/* Tables Tab */}
        <TabPane tabId='tables'>
          <TabTables
            semanticModel={semanticModel}
            setSemanticModel={setSemanticModel}
            setHasUnsavedChanges={setHasUnsavedChanges}
            handleEditTable={handleEditTable}
            schema={schema}
          />
        </TabPane>

        {/* Relationships Tab */}
        <TabPane tabId='relationships'>
            <TabRelationships
            semanticModel={semanticModel}
            handleDeleteRelationship={handleDeleteRelationship}
            handleEditRelationship={handleEditRelationship}
            handleAddRelationship={handleAddRelationship}
            />
        </TabPane>

        {/* YAML Tab */}
        {isStaffUser ? (
          <TabPane tabId='yaml'>
            <TabYaml
              semanticModel={semanticModel}
              yamlError={yamlError}
              handleYamlContentChange={handleYamlContentChange}
            />
          </TabPane>
        ) : null}
      </TabContent>

      {/* Table Modal */}
      <Modal isOpen={showTableModal} toggle={() => setShowTableModal(false)} size='lg'>
        <ModalHeader toggle={() => setShowTableModal(false)}>
          {editingTable?.name ? 'Edit Table' : 'Add Table'}
        </ModalHeader>
        <ModalBody>
          {editingTable ? (
            <TableConfigForm table={editingTable} schema={schema} onTableChange={setEditingTable} />
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color='primary' onClick={handleSaveTable}>
            Save Table
          </Button>
          <Button color='secondary' onClick={() => setShowTableModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Relationship Modal */}
      <Modal isOpen={showRelationshipModal} toggle={() => setShowRelationshipModal(false)} size='lg'>
        <ModalHeader toggle={() => setShowRelationshipModal(false)}>
          {editingRelationship?.name ? 'Edit Relationship' : 'Add Relationship'}
        </ModalHeader>
        <ModalBody>
          {editingRelationship ? (
            <RelationshipConfigForm
              relationship={editingRelationship}
              tables={semanticModel.tables}
              onRelationshipChange={setEditingRelationship}
            />
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color='primary' onClick={handleSaveRelationship}>
            Save Relationship
          </Button>
          <Button color='secondary' onClick={() => setShowRelationshipModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Bottom Save Section */}
      <div className='mt-4 p-3 border-top bg-light d-flex justify-content-end gap-2'>
        <Button color='success' onClick={handleSave} disabled={isSaving || !semanticModel.name.trim()}>
          {isSaving ? (
            <>
              <i className='fas fa-spinner fa-spin me-1' />
              Saving...
            </>
          ) : (
            <>
              <i className='fal fa-save me-1' />
              Save Configuration
            </>
          )}
        </Button>
        {hasUnsavedChanges && (
          <Button color='secondary' onClick={handleDiscard} disabled={isSaving}>
            Discard Changes
          </Button>
        )}
      </div>
    </div>
  );
};
